import { Box } from "../../core/components/Box";
import { useEffect, useMemo, useState } from "react";
import { QueuesViewModel } from "../../models/Queues";
import { WaitingServices } from "../../services/Waiting";

/* =========================
   Filter Types
========================= */
type FilterRange = "all" | "week" | "month" | "quarter";

export const Reports = () => {
  const [data, setData] = useState<QueuesViewModel[]>([]);
  const [filter, setFilter] = useState<FilterRange>("all");

  /* =========================
     DATA LOADING
  ========================= */

  const fetchWaiting = async () => {
    try {
      const waiting = await WaitingServices.getAllWaiting();
      setData(waiting);
    } catch (error) {
      console.error("Error fetching waiting", error);
    }
  };

  useEffect(() => {
    fetchWaiting();
  }, []);

  /* =========================
     DATE FILTER LOGIC
  ========================= */

  const filteredData = useMemo(() => {
    if (filter === "all") return data;

    const now = new Date();

    return data.filter((queue) => {
      const created = new Date(queue.queue_date); // ✅ change if field differs

      const diffTime = now.getTime() - created.getTime();
      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      if (filter === "week") return diffDays <= 7;
      if (filter === "month") return diffDays <= 30;
      if (filter === "quarter") return diffDays <= 90;

      return true;
    });
  }, [data, filter]);

  /* =========================
     UI
  ========================= */

  return (
    <Box>
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-2xl shadow-lg">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
            <p className="text-gray-600 mt-1">
              Real-time client queue monitoring
            </p>
          </div>

          <div className="bg-white px-5 py-3 rounded-xl shadow border">
            <p className="text-xs text-gray-500">Last Updated</p>
            <p className="font-mono text-xl font-bold">
              {new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
        </div>

        {/* FILTER BUTTONS */}
        <div className="flex flex-wrap gap-3 mb-6">
          {[
            { key: "all", label: "All" },
            { key: "week", label: "This Week" },
            { key: "month", label: "This Month" },
            { key: "quarter", label: "This Quarter" },
          ].map((btn) => (
            <button
              key={btn.key}
              onClick={() => setFilter(btn.key as FilterRange)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition
                ${
                  filter === btn.key
                    ? "bg-indigo-600 text-white border-indigo-600 shadow"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                }`}
            >
              {btn.label}
            </button>
          ))}
        </div>

        {/* TABLE */}
        <div className="bg-white rounded-xl shadow border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-700">
              <thead className="sticky top-0 bg-gradient-to-r from-amber-50 to-orange-50 uppercase text-xs font-semibold">
                <tr>
                  <th className="px-6 py-4">Queue</th>
                  <th className="px-6 py-4">Client</th>
                  <th className="px-6 py-4">Status</th>
                </tr>
              </thead>

              <tbody>
                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-3">
                        <span className="text-4xl">📭</span>
                        <p className="font-semibold">
                          No records for this range
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {filteredData.map((queues) => (
                  <tr
                    key={queues.id}
                    className="border-b hover:bg-indigo-50 transition"
                  >
                    {/* QUEUE */}
                    <td className="px-6 py-4">
                      <div className="w-16 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg flex items-center justify-center shadow">
                        <span className="text-white font-bold text-lg">
                          {queues.que_number}
                        </span>
                      </div>
                    </td>

                    {/* CLIENT */}
                    <td className="px-6 py-4">
                      <p className="font-bold">
                        {`${queues.first_name} ${queues.middle_name ?? ""} ${
                          queues.last_name
                        }`.toUpperCase()}
                      </p>

                      <span
                        className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                          queues.priorit === "senior" ||
                          queues.priorit === "pwd"
                            ? "bg-red-100 text-red-700"
                            : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {queues.priorit}
                      </span>
                    </td>

                    {/* STATUS */}
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                        ⏱ WAITING
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Box>
  );
};
